import {ExternalLinkIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, DatePicker, Link, Select} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {Filter} from '@/components/Filter';
import {getCurrencyOptions} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import {mapCountryListToCurrencies} from '@/utils/countries';

import DownloadOTCTradeCSVButton from './DownloadOTCTradeCSVButton';
import OTCActionButton from './OTCActionButton';
import type {OTCFiltersProps} from './types';

function OTCTradesFilters({
  filterCount,
  filtersToApply,
  currentFilters,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
}: OTCFiltersProps) {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const currencyOptions = mapCountryListToCurrencies(countryList ?? []);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    clearFilters();
  };

  return (
    <div className="mt-5 w-full">
      <div className="flex md:items-center flex-col md:flex-row justify-between">
        <div className="flex md:items-center space-x-2">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <Filter
                closeFilter={() => setShowFilters(false)}
                handleShowFilter={handleShowFilterChange}
                label="Filters"
                positionLeft={1}
                bodyClassName="w-full md:w-[300px]"
              >
                {showFilters && (
                  <div className="p-2 space-y-4 max-w-4xl mx-auto w-inherit col-span-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <DatePicker
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters?.('fromDate', date)
                        }
                        placeholderText="Select From Date"
                        width={100}
                      />

                      <DatePicker
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters?.('toDate', date)
                        }
                        placeholderText="Select To Date"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end">
                      <Button
                        onClick={handleClearFilters}
                        colorScheme="cyan"
                        variant="ghost"
                      >
                        Clear
                      </Button>

                      <Button
                        onClick={handleApplyFilter}
                        colorScheme="cyan"
                        className="ml-3"
                        disabled={!fromDate || !toDate}
                      >
                        Apply Filters {filterCount && ` (${filterCount})`}
                      </Button>
                    </div>
                  </div>
                )}
              </Filter>
            </div>
          </div>

          {/* Status Filter */}
          <Select
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              selectCurrentFilters('currency', e.target.value)
            }
            value={currentFilters.currency}
            name="currency"
            options={getCurrencyOptions(currencyOptions)}
            placeholder="Filter by Currency"
          />
        </div>

        <div className="flex justify-end flex-col md:flex-row md:items-center md:space-x-2 mt-5 md:mt-0 w-full">
          <div className="flex flex-wrap items-center space-x-2">
            <PermissionsProvider
              permission={AfriexAdminPermission.OTCVolumeRead}
            >
              <DownloadOTCTradeCSVButton filters={currentFilters} />
            </PermissionsProvider>

            <Link
              to={`/rates/trade-logs`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-max"
            >
              <Button
                colorScheme="cyan"
                variant="outline"
                leftIcon={<ExternalLinkIcon className="w-5 h-5" />}
              >
                View Logs
              </Button>
            </Link>
          </div>

          {/* This div now sits on its own row on mobile */}
          <div className="w-full md:w-auto mt-5 md:mt-0 flex justify-start md:justify-end">
            <PermissionsProvider
              permission={AfriexAdminPermission.OTCVolumeManage}
            >
              <OTCActionButton />
            </PermissionsProvider>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTCTradesFilters;
